import React, { useState } from 'react';

/**
 * ⛔⛔ RESET THE **PORTAL** SIGN-IN — the Cognito one. Card PARTNER-RESET-STAFF.
 *
 * ⚠⚠ TWO PARTNER CREDENTIAL CONTROLS HAVE BUTTONS: this one and "Reset
 * authenticator" (5 Oct 2026). Two OTHERS existed and both were no-ops that
 * looked like success, so both are retired and now refuse: the legacy "Reset
 * password" (LEGACY_RESET_RETIRED) wrote alliance_partner_users.password_hash,
 * and "Reset 2FA" (LEGACY_2FA_RESET_RETIRED) cleared totp_secret_encrypted —
 * neither column is read by partners.zignagenix.net, which authenticates
 * against Cognito. ⇒ A staff member no longer has to remember which of three
 * buttons is the real one; the two that remain do DIFFERENT things and say so.
 *
 * ⛔ A LOST AUTHENTICATOR IS A DIFFERENT BUTTON — "Reset authenticator", beside
 * this one (Card RESET-AUTHENTICATOR, 5 Oct 2026). This control resets the
 * PASSWORD and does not touch MFA. ⚠ It USED to say "escalate to Daniel", which
 * was true until that card and is the exact kind of line that outlives its
 * reason: a staff member reading a stale escalation sends a partner away from a
 * control that now exists. Both can be run on one call.
 *
 * ── WHAT THIS COMPONENT IS CAREFUL ABOUT ────────────────────────────────────
 *
 * ⛔ THE TEMPORARY PASSWORD EXISTS IN EXACTLY ONE PLACE: the PAGE's state,
 * between the server's reply and "Done". It is NEVER put into a URL, a toast, a
 * console line, an analytics event, or the parent's flash banner. The parent is
 * told only that a reset happened. There is no retrieval path — losing it means
 * running the reset again, which the runbook says plainly.
 *
 * ⚠⚠ THE PASSWORD NO LONGER LIVES HERE AT ALL — Card PORTAL-RESET-READOUT-d.
 * It is handed straight up to the page via `onCredential` and rendered by
 * partners/OneTimeCredentialPanel.js, which sits above everything a refetch can
 * unmount. This component used to hold it, and on 6 Oct a refetch destroyed it
 * 3 seconds after it appeared.
 *
 * ⛔⛔ THERE IS DELIBERATELY NO COPY BUTTON, AND IT WAS REMOVED ON PURPOSE.
 * navigator.clipboard.writeText puts the password into the OS clipboard, where
 * it outlives "Done", survives this component entirely, and is captured by
 * every clipboard-history tool. That directly contradicts both the claim above
 * and the runbook's "do not email it, text it, or paste it into a chat". The
 * password is read ALOUD, slowly, over the phone — that is the whole workflow,
 * and a Copy button exists only to support the thing the runbook forbids.
 * A test asserts this file contains no navigator.clipboard.
 *
 * ⛔ THE REASON IS STORED, AND STAFF WILL TYPE WHAT THEY ARE ASKED FOR. It goes
 * into alliance_partner_audit_log.payload and .summary verbatim. So the helper
 * text tells them to record WHAT they checked, never the VALUE — "verified DOB
 * on file", not the date of birth. Without that line the obvious thing to type
 * is the thing you just read off the screen, and the audit log becomes a second
 * copy of the identifiers it exists to protect.
 */

/** Must match MIN_REASON_LENGTH in database/partnerCognitoReset.js:61. */
export const MIN_REASON_LENGTH = 10;

/**
 * ⚠ THE RUNBOOK'S WORDING, NOT THE CODE'S. A staff member reads this sentence
 * and then reads §2 of docs/runbook_partner_signin_reset_2026-10-03.md; if the
 * two disagree they will not know which to believe. Every code the server can
 * return is here — a missing one would fall through to the raw code, which is
 * exactly the "what do I do now?" moment this mapping removes.
 */
export const ERROR_TEXT = Object.freeze({
  NO_PORTAL_IDENTITY:
    'No portal identity — this partner predates the portal or was never provisioned. '
    + 'They need provisioning, not a reset. Escalate to Daniel.',
  COGNITO_USER_NOT_FOUND:
    'Partner not found in the sign-in system. ⛔ Do not read out a password. Escalate to Daniel.',
  COGNITO_UNAVAILABLE:
    'Sign-in system unavailable — AWS did not answer. Wait a minute and retry; if it persists, escalate.',
  POOL_NOT_CONFIGURED:
    'Sign-in system unavailable — the portal sign-in pool is not configured on this server yet. Escalate.',
  PASSWORD_REJECTED:
    'The sign-in system rejected the generated password. Retry; if it persists, escalate.',
  USER_NOT_FOUND: 'That partner member no longer exists. Reload the page.',
  REASON_REQUIRED: `A reason is required — at least ${MIN_REASON_LENGTH} characters.`,
});

export function errorText(code) {
  return ERROR_TEXT[code] || `The reset did not happen (${code || 'unknown error'}). Escalate to Daniel.`;
}

export default function PortalSignInReset({ member, canReset, invoke, onResetComplete, onCredential }) {
  const [phase, setPhase] = useState('idle');   // idle | reason | confirm
  const [reason, setReason] = useState('');
  /**
   * ⛔⛔ THIS COMPONENT NO LONGER HOLDS THE PASSWORD — Card
   * PORTAL-RESET-READOUT-d. It used to, and a refetch of the org detail
   * unmounted this row and destroyed it (measured in Daniel's browser,
   * 23:30:53.825Z). The value now goes straight up to the page via
   * `onCredential`, which renders it in a panel nothing below can unmount.
   */
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // ⛔ THE SERVER IS THE GATE. This only decides whether to OFFER the control —
  // a staff member without the permission is not invited to press something
  // that will be refused.
  if (!canReset) return null;

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= MIN_REASON_LENGTH;

  function reset() {
    setPhase('idle'); setReason(''); setErr(null);
  }

  async function doReset() {
    if (!reasonOk || busy) return;
    setBusy(true); setErr(null);
    try {
      const call = invoke || ((c, p) => window.zignagenixAPI.invoke(c, p));
      const r = await call('alliance_partner:members:reset-portal-signin', {
        partnerUserId: member.id,
        reason: trimmed,
      });
      if (!r || r.error) {
        // ⛔ NO PASSWORD ON AN ERROR PATH, EVER — not even a partial one. The
        // runbook's standing instruction is "do not read out a password anyway
        // in case it worked", and the UI must not tempt that.
        setErr(errorText(r && r.code));
        setPhase('reason');
        return;
      }
      /**
       * ⛔ STRAIGHT UP TO THE PAGE. The value is never stored in this component,
       * so nothing that unmounts this row can destroy it. `auditWritten:false`
       * travels with it: the password is real and must still be read out, and
       * the gap is reported beside it rather than hidden.
       */
      if (onCredential) {
        onCredential({ email: member.email, password: r.temporaryPassword, auditGap: r.auditWritten === false });
      }
      setPhase('idle'); setReason('');
      // ⚠ The parent learns THAT it happened, never the value.
      if (onResetComplete) onResetComplete({ partnerUserId: member.id });
    } catch (e) {
      setErr(errorText(null));
      setPhase('reason');
    } finally {
      setBusy(false);
    }
  }


  const btn = (bg, fg, bd) => ({
    padding: '4px 10px', fontSize: 11, fontWeight: 600, background: bg, color: fg,
    border: `1px solid ${bd}`, borderRadius: 5, cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.5 : 1,
  });

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={() => setPhase('reason')}
        aria-label={`Reset portal sign-in for ${member.email}`}
        style={btn('#fff', '#8A3FFC', '#d9c2fb')}
      >
        Reset portal sign-in
      </button>
    );
  }

  return (
    <div
      data-testid="portal-reset-panel"
      style={{ marginTop: 8, padding: 10, background: '#F6F2FF', border: '1px solid #d9c2fb', borderRadius: 6, flex: '1 1 100%' }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#5B2BB5', marginBottom: 6 }}>
        Reset PORTAL sign-in for {member.email}
      </div>
      <div style={{ fontSize: 11, color: '#6C4BB6', marginBottom: 8 }}>
        This resets the <strong>password</strong> for <strong>partners.zignagenix.net</strong>.
        It does <strong>not</strong> re-enrol their authenticator — if they have lost their
        phone, use <strong>&ldquo;Reset authenticator&rdquo;</strong> as well. Both can be done
        on one call.
      </div>

      {(
        <>
          <label htmlFor="portal-reset-reason" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>
            Reason (required)
          </label>
          <textarea
            id="portal-reset-reason"
            value={reason}
            disabled={busy}
            onChange={(e) => { setReason(e.target.value); setErr(null); }}
            rows={2}
            placeholder="Partner called 3 Oct, forgot password, verified against the org and the email on file."
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, border: '1px solid #CED4DA', borderRadius: 5 }}
          />
          {/* ⛔ THE LINE THAT KEEPS IDENTIFIERS OUT OF THE AUDIT LOG. */}
          <div data-testid="reason-helper" style={{ marginTop: 4, fontSize: 11, color: '#868E96' }}>
            Describe <strong>what you checked</strong>, never the value. No date of birth, no
            phone number, no patient names — this reason is stored in the audit log.
            Minimum {MIN_REASON_LENGTH} characters.
          </div>

          {err && (
            <div data-testid="portal-reset-error" role="alert" style={{ marginTop: 8, padding: 8, background: '#FFF5F5', border: '1px solid #F5C2C7', borderRadius: 5, fontSize: 11, color: '#842029' }}>
              {err}
            </div>
          )}

          {phase === 'confirm' && (
            <div data-testid="portal-reset-confirm" style={{ marginTop: 8, padding: 8, background: '#FFF8E1', border: '1px solid #F0B429', borderRadius: 5, fontSize: 11, color: '#8A6100' }}>
              This issues a working temporary password for <strong>{member.email}</strong> and
              shows it <strong>once</strong>. Make sure you have verified who you are speaking to.
            </div>
          )}

          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {phase === 'reason' && (
              <button type="button" disabled={!reasonOk || busy} onClick={() => setPhase('confirm')}
                style={{ ...btn('#8A3FFC', '#fff', '#8A3FFC'), opacity: !reasonOk || busy ? 0.5 : 1 }}>
                Continue
              </button>
            )}
            {phase === 'confirm' && (
              <button type="button" data-testid="portal-reset-confirm-button" disabled={busy} onClick={doReset}
                style={btn('#8A3FFC', '#fff', '#8A3FFC')}>
                {busy ? 'Resetting…' : 'Yes — reset the portal sign-in'}
              </button>
            )}
            <button type="button" disabled={busy} onClick={reset} style={btn('#fff', '#495057', '#CED4DA')}>
              Cancel
            </button>
          </div>
        </>
      )}

    </div>
  );
}
